import { useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getCurrentWeekRates } from '../utils/exchangeRates';
import InvoicePanel from './InvoicePanel';

// ── Padelante bespoke quote ──────────────────────────────────────────────────
// Padelante tours don't use the MST price-code engine. Instead a padel tour is
// a short, fixed cost sheet: four customer-facing lines (each an MST cost + a
// customer price) plus an internal referral fee (a commission paid to whoever
// registered the group — a real cost to us, but never charged to the customer).
//
// The quote is stored on the request under `padelanteQuote`. We also write a
// compatible `quoteDetails` summary so the shared InvoicePanel (and the
// "Quote Sent" gating) keep working unchanged.

const SYM = '£';
const VAT_RETAINED = 0.79; // Padelante is Ireland-based — 21% EU VAT.
const CUR_SYM = { GBP: '£', EUR: '€' };

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function fmtCur(n, currency) {
  const v = Number(n) || 0;
  return `${CUR_SYM[currency] || '£'}${v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// The four fixed customer-facing lines, in display order.
const CUSTOMER_LINES = [
  { key: 'hotel_food', label: 'Hotel & Food' },
  { key: 'flights',    label: 'Flights' },
  { key: 'transport',  label: 'Transport' },
  { key: 'padel',      label: 'Padel' },
];

function fmt(n) {
  const v = Number(n) || 0;
  return `${SYM}${v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default function PadelanteQuote({ requestId, request, onSendQuoteLetter, sendingQuoteLetter, quoteLetterSent, quoteLetterError }) {
  const payingPlayers = num(request?.participants) || 0;
  const hasSavedQuote = !!request?.quoteDetails;

  // Cost sheet state. Each line has a `basis` ('group' | 'player') and an MST
  // `currency` ('GBP' | 'EUR'). Customer prices are always entered in GBP.
  const [lines, setLines] = useState(() => blankLines());
  const [referralFee, setReferralFee] = useState('');
  const [referralBasis, setReferralBasis] = useState('group');
  const [referralCurrency, setReferralCurrency] = useState('GBP');
  const [customerPriceOverride, setCustomerPriceOverride] = useState(null); // total £, or null
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  // Exchange rates. `rates` = this week's pull; `activeRate` (EUR→GBP) + its
  // week are what the sheet actually converts with. A saved quote LOCKS the rate
  // it was saved at, so re-opening it doesn't silently drift when Monday's pull
  // lands — staff can opt in to the newer rate with the "Use this week's rate"
  // button, which re-bases the calculation.
  const lockedRate = request?.padelanteQuote?.eurGbpRate;
  const [rates, setRates] = useState(null);
  const [activeRate, setActiveRate] = useState(lockedRate != null ? Number(lockedRate) : 1);
  const [activeRateWeek, setActiveRateWeek] = useState(request?.padelanteQuote?.rateWeek || null);

  function blankLines() {
    const o = {};
    CUSTOMER_LINES.forEach((l) => { o[l.key] = { mst: '', customer: '', basis: 'group', currency: 'GBP' }; });
    return o;
  }

  // A single entered amount → its group total, given the line's basis.
  function toGroupTotal(value, basis) {
    return basis === 'player' ? num(value) * payingPlayers : num(value);
  }

  // An MST amount (as entered) → its group total in GBP, applying basis then the
  // active EUR→GBP rate when the line is priced in euros.
  function mstGroupGBP(value, basis, currency) {
    const nativeGroup = toGroupTotal(value, basis);
    return currency === 'EUR' ? round2(nativeGroup * activeRate) : nativeGroup;
  }

  // Load this week's rates once. Only adopt them as the active rate when there's
  // no locked rate from a previously saved quote.
  useEffect(() => {
    let alive = true;
    getCurrentWeekRates().then((r) => {
      if (!alive) return;
      setRates(r);
      if (lockedRate == null) {
        setActiveRate(Number(r.EUR_GBP) || 1);
        setActiveRateWeek(r.weekStart);
      }
    }).catch(console.error);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate from a previously saved padelanteQuote.
  useEffect(() => {
    const pq = request?.padelanteQuote;
    if (!pq) return;
    const next = blankLines();
    (pq.lines || []).forEach((l) => {
      if (next[l.key]) next[l.key] = { mst: String(l.mst ?? ''), customer: String(l.customer ?? ''), basis: l.basis || 'group', currency: l.currency || 'GBP' };
    });
    setLines(next);
    setReferralFee(pq.referralFee != null ? String(pq.referralFee) : '');
    setReferralBasis(pq.referralBasis || 'group');
    setReferralCurrency(pq.referralCurrency || 'GBP');
    setCustomerPriceOverride(pq.customerPriceOverride != null ? pq.customerPriceOverride : null);
    if (pq.eurGbpRate != null) { setActiveRate(Number(pq.eurGbpRate)); setActiveRateWeek(pq.rateWeek || null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.padelanteQuote]);

  const totals = useMemo(() => {
    const linesCustomerSum = CUSTOMER_LINES.reduce((s, l) => s + toGroupTotal(lines[l.key]?.customer, lines[l.key]?.basis), 0);
    const linesMstSum = CUSTOMER_LINES.reduce((s, l) => s + mstGroupGBP(lines[l.key]?.mst, lines[l.key]?.basis, lines[l.key]?.currency), 0);
    const referral = mstGroupGBP(referralFee, referralBasis, referralCurrency);
    const totalMST = linesMstSum + referral;
    const totalCustomer = customerPriceOverride !== null ? customerPriceOverride : linesCustomerSum;
    const perPlayer = payingPlayers > 0 ? totalCustomer / payingPlayers : 0;
    const grossProfit = totalCustomer - totalMST;
    const postVatProfit = grossProfit * VAT_RETAINED;
    const marginPct = totalCustomer > 0 ? ((grossProfit / totalCustomer) * 100).toFixed(1) : '0.0';
    return { linesCustomerSum, linesMstSum, referral, totalMST, totalCustomer, perPlayer, grossProfit, postVatProfit, marginPct };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, referralFee, referralBasis, referralCurrency, customerPriceOverride, payingPlayers, activeRate]);

  function updateLine(key, field, value) {
    setLines((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
    setSavedAt(null);
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      // Store the amounts AS ENTERED plus each line's basis, so the sheet
      // round-trips exactly on reload.
      const savedLines = CUSTOMER_LINES.map((l) => ({
        key: l.key,
        label: l.label,
        mst: num(lines[l.key]?.mst),
        customer: num(lines[l.key]?.customer),
        basis: lines[l.key]?.basis || 'group',
        currency: lines[l.key]?.currency || 'GBP',
      }));

      const padelanteQuote = {
        lines: savedLines,
        referralFee: num(referralFee),
        referralBasis,
        referralCurrency,
        customerPriceOverride: customerPriceOverride,
        totalCostToMST: totals.totalMST,
        totalCostToCustomer: totals.totalCustomer,
        costPerPersonToCustomer: totals.perPlayer,
        payingPlayers,
        // Lock the EUR→GBP rate this quote was priced at.
        eurGbpRate: activeRate,
        rateWeek: activeRateWeek || null,
      };

      // Compatible summary for the shared InvoicePanel + status gating + PDFs.
      // Breakdown carries GROUP totals in GBP (basis + FX already applied).
      const quoteDetails = {
        totalCostToMST: totals.totalMST,
        totalCostToCustomer: totals.totalCustomer,
        costPerPersonToCustomer: totals.perPlayer,
        payingPlayers,
        freePlaces: 0,
        exchangeRateUsed: activeRate,
        rateWeek: activeRateWeek || null,
        breakdown: savedLines.map((l) => {
          const tags = [l.basis === 'player' ? 'per player' : null, l.currency === 'EUR' ? 'EUR cost' : null].filter(Boolean);
          return {
            categoryName: tags.length ? `${l.label} (${tags.join(', ')})` : l.label,
            pricingType: 'flat',
            mstCurrency: 'GBP',
            costToMST: mstGroupGBP(l.mst, l.basis, l.currency),
            costToMSTConverted: mstGroupGBP(l.mst, l.basis, l.currency),
            ...(l.currency === 'EUR' ? { costToMSTEUR: toGroupTotal(l.mst, l.basis) } : {}),
            costToCustomer: toGroupTotal(l.customer, l.basis),
          };
        }),
      };

      await updateDoc(doc(db, 'requests', requestId), {
        currency: 'GBP',
        padelanteQuote,
        quoteDetails,
        quoteNeedsUpdate: false,
      });
      setSavedAt(Date.now());
    } catch (e) {
      console.error('Padelante quote save failed:', e);
      setError('Could not save the quote — please try again.');
    } finally {
      setSaving(false);
    }
  }

  const anyEuro = referralCurrency === 'EUR' || CUSTOMER_LINES.some((l) => lines[l.key]?.currency === 'EUR');

  // Per-player slider bounds.
  const perPlayerFloor = payingPlayers > 0 ? totals.totalMST / payingPlayers : 0;
  const sliderMin = Math.floor(perPlayerFloor);
  const sliderMax = Math.max(sliderMin + 1, Math.ceil((totals.linesCustomerSum / (payingPlayers || 1)) * 1.5) || 1);
  const sliderVal = payingPlayers > 0 ? Math.round(totals.totalCustomer / payingPlayers) : 0;

  const tiles = [
    { label: 'MST cost',        value: fmt(totals.totalMST),        color: '#C0392B', bg: '#FBEAE8', border: 'rgba(192,57,43,.2)' },
    { label: 'Customer price',  value: fmt(totals.totalCustomer),   sub: customerPriceOverride !== null ? 'Adjusted' : null, color: '#1A4A8A', bg: '#EBF2FC', border: 'rgba(26,74,138,.2)' },
    { label: 'Per person',      value: fmt(totals.perPlayer),       sub: `${payingPlayers} paying`, color: '#0D2240', bg: '#F9FAFB', border: '#E5E7EB' },
    { label: 'Post-VAT profit', value: fmt(totals.postVatProfit),   sub: `${totals.marginPct}% margin · after 21% VAT`, color: totals.postVatProfit >= 0 ? '#1A7A4A' : '#C0392B', bg: totals.postVatProfit >= 0 ? '#D1FAE5' : '#FBEAE8', border: totals.postVatProfit >= 0 ? 'rgba(26,122,74,.2)' : 'rgba(192,57,43,.2)' },
  ];

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Padelante Quote</h3>
          {payingPlayers === 0 && (
            <span className="text-[11px] text-[#C0392B]">Set paying guests in Overview → Group Info for per-player figures.</span>
          )}
        </div>
        <p className="text-[12px] text-[#9CA3AF] mb-3">Padel · Alicante · customer pays in {SYM}. Referral fee is an internal cost and is never charged to the customer.</p>

        {/* Exchange-rate banner */}
        {anyEuro && (
          <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] bg-[#F9FAFB] border border-[#E5E7EB] rounded-[10px] px-4 py-2.5">
            <span className="text-[#374151] font-semibold">€ costs converted at €1 = {SYM}{Number(activeRate).toFixed(4)}</span>
            {activeRateWeek && <span className="text-[#9CA3AF]">· week of {activeRateWeek} (locked)</span>}
            {rates && Number(rates.EUR_GBP) !== Number(activeRate) && (
              <button type="button"
                onClick={() => { setActiveRate(Number(rates.EUR_GBP) || 1); setActiveRateWeek(rates.weekStart); setSavedAt(null); }}
                className="text-pb font-semibold hover:text-navy transition">
                Use this week's rate (€1 = {SYM}{Number(rates.EUR_GBP).toFixed(4)})
              </button>
            )}
            {rates?.isStale && <span className="text-[#C0392B]">· this week's rate not pulled yet</span>}
          </div>
        )}

        {/* Cost sheet */}
        <div className="overflow-x-auto -mx-1 mb-5">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="px-1 py-2 text-left text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Line</th>
                <th className="px-1 py-2 text-left text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Priced</th>
                <th className="px-1 py-2 text-right text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">MST cost</th>
                <th className="px-1 py-2 text-right text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Customer price</th>
              </tr>
            </thead>
            <tbody>
              {CUSTOMER_LINES.map((l) => {
                const basis = lines[l.key]?.basis || 'group';
                const currency = lines[l.key]?.currency || 'GBP';
                const mstGBP = mstGroupGBP(lines[l.key]?.mst, basis, currency);
                const showHint = basis === 'player' || currency === 'EUR';
                return (
                  <tr key={l.key} className="border-b border-[#F3F4F6]">
                    <td className="px-1 py-2.5 text-[13px] font-medium text-[#374151]">
                      {l.label}
                      {showHint && (
                        <span className="block text-[10.5px] font-normal text-[#9CA3AF]">
                          Group MST: {fmtCur(toGroupTotal(lines[l.key]?.mst, basis), currency)}{currency === 'EUR' ? ` ≈ ${fmt(mstGBP)}` : ''} · charge {fmt(toGroupTotal(lines[l.key]?.customer, basis))}
                        </span>
                      )}
                    </td>
                    <td className="px-1 py-2">
                      <BasisSelect value={basis} onChange={(v) => updateLine(l.key, 'basis', v)} />
                    </td>
                    <td className="px-1 py-2">
                      <div className="flex items-center justify-end gap-1.5">
                        <CurrencySelect value={currency} onChange={(v) => updateLine(l.key, 'currency', v)} />
                        <MoneyInput value={lines[l.key]?.mst} onChange={(v) => updateLine(l.key, 'mst', v)} symbol={CUR_SYM[currency]} />
                      </div>
                    </td>
                    <td className="px-1 py-2 text-right">
                      <MoneyInput value={lines[l.key]?.customer} onChange={(v) => updateLine(l.key, 'customer', v)} accent />
                    </td>
                  </tr>
                );
              })}
              {/* Referral fee — internal cost only */}
              <tr className="border-b border-[#F3F4F6] bg-[#FFFBEB]/60">
                <td className="px-1 py-2.5 text-[13px] font-medium text-[#374151]">
                  Referral fee
                  <span className="block text-[10.5px] font-normal text-[#9CA3AF]">
                    Internal — commission to the registrar, not charged
                    {(referralBasis === 'player' || referralCurrency === 'EUR') &&
                      ` · Group: ${fmtCur(toGroupTotal(referralFee, referralBasis), referralCurrency)}${referralCurrency === 'EUR' ? ` ≈ ${fmt(mstGroupGBP(referralFee, referralBasis, referralCurrency))}` : ''}`}
                  </span>
                </td>
                <td className="px-1 py-2">
                  <BasisSelect value={referralBasis} onChange={(v) => { setReferralBasis(v); setSavedAt(null); }} />
                </td>
                <td className="px-1 py-2">
                  <div className="flex items-center justify-end gap-1.5">
                    <CurrencySelect value={referralCurrency} onChange={(v) => { setReferralCurrency(v); setSavedAt(null); }} />
                    <MoneyInput value={referralFee} onChange={(v) => { setReferralFee(v); setSavedAt(null); }} symbol={CUR_SYM[referralCurrency]} />
                  </div>
                </td>
                <td className="px-1 py-2 text-right text-[12px] text-[#9CA3AF]">—</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#E5E7EB]">
                <td className="px-1 py-2.5 text-[12px] font-semibold text-[#111827]">Totals (group)</td>
                <td />
                <td className="px-1 py-2.5 text-right text-[13px] font-bold text-[#C0392B] tabular-nums">{fmt(totals.totalMST)}</td>
                <td className="px-1 py-2.5 text-right text-[13px] font-bold text-pb tabular-nums">{fmt(totals.totalCustomer)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Adjust price per player */}
        {payingPlayers > 0 && (
          <div className="mb-5 bg-[#F0F7FF] border border-pb/20 rounded-[10px] px-4 py-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[12px] font-semibold text-pb">Adjust Price Per Player</p>
              {customerPriceOverride !== null && (
                <button type="button" onClick={() => { setCustomerPriceOverride(null); setSavedAt(null); }}
                  className="text-[11px] text-[#9CA3AF] hover:text-dr transition">Reset to line total</button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <input type="range" min={sliderMin} max={sliderMax} step={1} value={sliderVal}
                onChange={(e) => { setCustomerPriceOverride(Number(e.target.value) * payingPlayers); setSavedAt(null); }}
                className="flex-1 accent-pb" />
              <div className="flex items-center border border-[#E5E7EB] rounded-[8px] overflow-hidden bg-white">
                <span className="px-2 py-1.5 text-[13px] text-[#6B7280] bg-surface border-r border-[#E5E7EB]">{SYM}</span>
                <input type="number" step="1" min={sliderMin} value={sliderVal}
                  onChange={(e) => { setCustomerPriceOverride(Number(e.target.value) * payingPlayers); setSavedAt(null); }}
                  className="w-24 px-2 py-1.5 text-[13px] font-semibold text-pb focus:outline-none" />
              </div>
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-1.5">Floor: {fmt(Math.ceil(perPlayerFloor))}/player (MST cost). Adjusts total and profit in real time.</p>
          </div>
        )}

        {/* Summary tiles */}
        <div className="grid grid-cols-2 1080:grid-cols-4 gap-3 mb-5">
          {tiles.map((tile) => (
            <div key={tile.label} className="rounded-[11px] p-[14px]" style={{ background: tile.bg, border: `1px solid ${tile.border}` }}>
              <p className="text-[11.5px] font-semibold text-[#6B7280] mb-[6px]">{tile.label}</p>
              <p className="text-[19px] font-extrabold tabular-nums leading-none" style={{ color: tile.color }}>{tile.value}</p>
              {tile.sub && <p className="text-[11px] text-[#9CA3AF] mt-1">{tile.sub}</p>}
            </div>
          ))}
        </div>

        <div className="flex gap-3 items-center flex-wrap">
          <button onClick={save} disabled={saving}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60">
            {saving ? 'Saving…' : 'Save Quote'}
          </button>
          {onSendQuoteLetter && (
            <button type="button" onClick={onSendQuoteLetter} disabled={!hasSavedQuote || sendingQuoteLetter}
              className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-[#1A7A4A] hover:bg-[#155e38] rounded-[8px] transition disabled:opacity-40"
              title={!hasSavedQuote ? 'Save a quote first' : 'Send the PDF quote letter to the customer'}>
              {sendingQuoteLetter ? 'Sending…' : 'Send Quote Letter'}
            </button>
          )}
          {quoteLetterSent && <span className="text-[12px] text-[#1A7A4A] font-semibold">Quote letter sent!</span>}
          {savedAt && !quoteLetterSent && <span className="text-[12px] text-[#1A7A4A] font-semibold">Saved.</span>}
          {error
            ? <p className="text-[12px] text-dr">{error}</p>
            : quoteLetterError
              ? <p className="text-[12px] text-dr">{quoteLetterError}</p>
              : <p className="text-[12px] text-[#9CA3AF]">Save, then send the quote letter or raise invoices below.</p>}
        </div>
      </div>

      {/* Invoices — shared panel (already Padelante-aware → PAD- invoices) */}
      <InvoicePanel requestId={requestId} request={request} brand="padelante" />
    </div>
  );
}

function BasisSelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-[#E5E7EB] rounded-[8px] px-2 py-1.5 text-[12.5px] text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
    >
      <option value="group">Per group</option>
      <option value="player">Per player</option>
    </select>
  );
}

function CurrencySelect({ value, onChange }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-[#E5E7EB] rounded-[8px] px-1.5 py-1.5 text-[12.5px] text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-pb"
      title="Currency of the MST cost"
    >
      <option value="GBP">£</option>
      <option value="EUR">€</option>
    </select>
  );
}

function MoneyInput({ value, onChange, accent, symbol }) {
  return (
    <div className="inline-flex items-center border border-[#E5E7EB] rounded-[8px] overflow-hidden bg-white">
      <span className="px-2 py-1.5 text-[12px] text-[#6B7280] bg-surface border-r border-[#E5E7EB]">{symbol || SYM}</span>
      <input
        type="number" step="1" min="0" inputMode="decimal"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="0"
        className={`w-24 px-2 py-1.5 text-[13px] font-semibold text-right focus:outline-none ${accent ? 'text-pb' : 'text-[#111827]'}`}
      />
    </div>
  );
}
